import React, { useEffect, useState } from "react";
import { Table, Input, Button, Space, Popconfirm, message, Tag, Form, Select, Image } from "antd";
import { EditOutlined, DeleteOutlined, FilterOutlined, DownloadOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import startCase from "lodash/startCase";
import { Typography } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import InventoryForm from "../components/Inventory/InventoryForm";
import RequirePermission from "../components/Common/RequirePermission";
import ClearFiltersButton from "../components/Common/ClearFiltersButton";
const { Title } = Typography;

const STATUS_COLORS = {
  in_use: "green",
  not_in_use: "default",
  sent_for_servicing: "gold",
  under_repair: "orange",
  decommissioned: "red",
};

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInventory, setEditingInventory] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [cityOptions, setCityOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  const [selectedCity, setSelectedCity] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const [createPermission, setCreatePermission] = useState(false);
  const [editPermission, setEditPermission] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterText, setFilterText] = useState("");

  const handleSearch = (e) => {
    setFilterText(e.target.value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilterText("");
    setSelectedCity("");
    setSelectedLocation("");
    setSelectedBrand("");
    setSelectedStatus("");
    setCurrentPage(1);
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const fetchMasterData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/inventory/masters`);
      const data = response.data.data;
      setBrandOptions(data.brands);
      setStatusOptions(data.status);
      setCityOptions(data.cities);
      setLocationOptions(data.facilities.locations);
    } catch (err) {
      console.error(err.message);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(process.env.REACT_APP_BASE_URL + `/api/inventory`, {
        params: {
          filter: filterText,
          city: selectedCity,
          location: selectedLocation,
          brand_id: selectedBrand,
          status: selectedStatus,
          limit: pageSize,
          page: currentPage,
        },
      });
      setTotalRecords(response.data.data.total);
      setInventory(response.data.data.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching inventory data", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/inventory/${id}`);
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Inventory item deleted");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/inventory/download`,
        {
          params: {
            filter: filterText,
            city: selectedCity,
            location: selectedLocation,
            brand_id: selectedBrand,
            status: selectedStatus,
          },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "inventory.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloading(false);
    } catch (error) {
      setDownloading(false);
      message.error("Failed to download inventory");
    }
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    if (storedUser) {
      const permissions = storedUser.permissions;
      setCreatePermission(parseInt(permissions["signage.inventory_create"]) === 1);
      setEditPermission(parseInt(permissions["signage.inventory_edit"]) === 1);
      setDeletePermission(parseInt(permissions["signage.inventory_delete"]) === 1);
    }
    fetchMasterData();
  }, []);

  useEffect(() => {
    const fetchTimer = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(fetchTimer);
  }, [currentPage, pageSize, filterText, selectedCity, selectedLocation, selectedBrand, selectedStatus]);

  const columns = [
    { title: "Brand", dataIndex: "brand_title" },
    {
      title: "Serial Number",
      dataIndex: "serial_number",
      render: (text, record) => (
        <Space>
          <span>{text || "-"}</span>
          {record.serial_number_image && (
            <Image src={record.serial_number_image} width={32} height={32} style={{ objectFit: "cover" }} />
          )}
        </Space>
      ),
    },
    { title: "Model", dataIndex: "model" },
    {
      title: "Facility",
      dataIndex: "location_name",
      render: (text, record) => (
        <span className="text-small">
          {text} {record.city_name ? `(${record.city_name})` : ""}
        </span>
      ),
    },
    { title: "Position", dataIndex: "position" },
    {
      title: "TV Photo",
      dataIndex: "tv_photo",
      render: (text) => (text ? <Image src={text} width={48} height={48} style={{ objectFit: "cover" }} /> : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>{startCase(status)}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (text) => <span className="text-small">{dayjs(text).format("DD-MM-YYYY HH:mm")}</span>,
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      render: (text) => <span className="text-small">{text ? dayjs(text).format("DD-MM-YYYY HH:mm") : "-"}</span>,
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {editPermission && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingInventory(record);
                setIsModalOpen(true);
              }}
            />
          )}
          {deletePermission && (
            <Popconfirm
              title="Are you sure to delete this inventory item?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <RequirePermission permission="signage.inventory_view">
      <div style={{ padding: "1rem" }}>
        <Helmet>
          <title>{`${process.env.REACT_APP_NAME} | Inventory`}</title>
        </Helmet>
        <Title level={3} style={{ marginBottom: "1rem" }}>
          Inventory
        </Title>
        <div className="d-sm-block d-md-flex justify-content-between mb-3">
          <Form layout="inline">
            <Form.Item>
              <Input.Search
                placeholder="Search serial number / model"
                value={filterText}
                className="me-1"
                onChange={handleSearch}
                style={{ width: 220 }}
              />
            </Form.Item>
            <Form.Item>
              <Button
                icon={<FilterOutlined />}
                type={showFilters ? "primary" : "default"}
                onClick={() => setShowFilters((prev) => !prev)}
              >
                Filters
                {(selectedCity || selectedLocation || selectedBrand || selectedStatus) && (
                  <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                    {[selectedCity, selectedLocation, selectedBrand, selectedStatus].filter(Boolean).length}
                  </Tag>
                )}
              </Button>
            </Form.Item>
          </Form>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleDownload} loading={downloading}>
              Export
            </Button>
            {createPermission && (
              <Button
                type="primary"
                onClick={() => {
                  setEditingInventory(null);
                  setIsModalOpen(true);
                }}
              >
                Add New Inventory
              </Button>
            )}
          </Space>
        </div>

        {showFilters && (
          <Form layout="inline" className="mb-3">
            <Form.Item label="City">
              <Select
                value={selectedCity || undefined}
                allowClear
                showSearch
                placeholder="Select City"
                onChange={(v) => setSelectedCity(v || "")}
                style={{ width: 180 }}
                filterOption={(input, option) =>
                  (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                }
              >
                {cityOptions.map((city) => (
                  <Select.Option key={city.id} value={city.id}>
                    {city.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Location">
              <Select
                value={selectedLocation || undefined}
                allowClear
                showSearch
                placeholder="Select Location"
                onChange={(v) => setSelectedLocation(v || "")}
                style={{ width: 200 }}
                filterOption={(input, option) =>
                  (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                }
              >
                {locationOptions.map((loc) => (
                  <Select.Option key={loc.id} value={loc.id}>
                    {loc.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Brand">
              <Select
                value={selectedBrand || undefined}
                allowClear
                showSearch
                placeholder="Select Brand"
                onChange={(v) => setSelectedBrand(v || "")}
                style={{ width: 180 }}
                filterOption={(input, option) =>
                  (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                }
              >
                {brandOptions.map((brand) => (
                  <Select.Option key={brand.id} value={brand.id}>
                    {brand.title}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Status">
              <Select
                value={selectedStatus || undefined}
                allowClear
                placeholder="Select Status"
                onChange={(v) => setSelectedStatus(v || "")}
                style={{ width: 180 }}
              >
                {statusOptions.map((status) => (
                  <Select.Option key={status.value} value={status.value}>
                    {status.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item style={{ marginLeft: "auto", alignSelf: "flex-end" }}>
              <ClearFiltersButton onClick={handleClearFilters} />
            </Form.Item>
          </Form>
        )}

        <Table
          loading={loading}
          columns={columns}
          dataSource={inventory}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} inventory items`,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50", "100"],
          }}
        />

        {isModalOpen && (
          <InventoryForm
            isModalOpen={isModalOpen}
            editingInventory={editingInventory}
            closeModal={() => setIsModalOpen(false)}
            handleUpdateList={() => fetchData()}
          />
        )}
      </div>
    </RequirePermission>
  );
};

export default Inventory;
